/* eslint-disable @typescript-eslint/no-explicit-any */
import { motion, AnimatePresence } from "framer-motion";
import { Card, CardTitle, CardDescription } from "@/components/ui/card";
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
  FormDescription,
} from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { useFormContext } from "react-hook-form";
import {
  ArrowLeft,
  VideoOff,
  Loader2,
  RefreshCcw,
  Clock,
  AlertCircle,
} from "lucide-react";
import { useReactMediaRecorder } from "react-media-recorder";
import { cn } from "@/lib/utils";
import { Show } from "@/components/atoms/show";
import { useState, useEffect } from "react";
import { CollectionFormScreenProps } from "../../types";
import { getCollectionFormStyles } from "../../utils/extract-review-form-styles";
import { Progress } from "@/components/ui/progress";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { StarRating } from "@/components/atoms/rating";
import { TestimonialFormTypes } from "../../utils/schema";

export const VideoResponseScreen = ({
  data,
  onChangeStep,
}: CollectionFormScreenProps) => {
  const form = useFormContext<TestimonialFormTypes>();
  const [recordingTime, setRecordingTime] = useState(0);
  const [countdown, setCountdown] = useState<number | null>(null);
  const [hasRecordingEnded, setHasRecordingEnded] = useState(false);
  const [remountKey, setRemountKey] = useState(0);
  const maxDuration = Number(data.settings.maxVideoDuration) || 60;
  const styles = getCollectionFormStyles(data);

  const {
    status,
    startRecording,
    stopRecording,
    mediaBlobUrl,
    previewStream,
    clearBlobUrl,
  } = useReactMediaRecorder({
    video: true,
    askPermissionOnMount: true,
    onStop: (_, blob) => {
      form.setValue("media", [blob] as any[]);
      setHasRecordingEnded(true);
    },
  });

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (status === "recording") {
      interval = setInterval(() => {
        setRecordingTime((prev) => {
          if (prev >= maxDuration) {
            handleStopRecording();
            return prev;
          }
          return prev + 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, maxDuration]);

  const handleStopRecording = () => {
    stopRecording();
    setHasRecordingEnded(true);
  };

  const resetRecording = () => {
    clearBlobUrl();
    form.setValue("media", []);
    setRecordingTime(0);
    setHasRecordingEnded(false);
    setRemountKey((prev) => prev + 1);
  };

  const startCountdownAndRecord = () => {
    setCountdown(3);
    const countdownInterval = setInterval(() => {
      setCountdown((prev) => {
        if (prev === 1) {
          clearInterval(countdownInterval);
          startRecording();
          return null;
        }
        return prev ? prev - 1 : null;
      });
    }, 1000);
  };

  const onNextStep = () => {
    onChangeStep("customerForm");
    form.setValue("type", "video");
    form.setValue("source", "text");
    form.setValue("content", "");
  };

  return (
    <Card
      className={cn(styles.cardPadding, styles.cardSpacing, "testify-max-w-xl")}
      style={{
        backgroundColor: styles["secondaryColor"],
        color: styles["secondaryTextColor"],
      }}
    >
      {/* Header */}
      <div className="testify-space-y-4">
        <div className="testify-flex testify-items-center testify-justify-between">
          <div className="testify-flex testify-items-center testify-gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onChangeStep("welcome")}
            >
              <ArrowLeft className="testify-h-5 testify-w-5" />
            </Button>
            <CardTitle className="testify-text-xl sm:testify-text-2xl md:testify-text-3xl">
              {data.responsePage.title}
            </CardTitle>
          </div>
          {status === "recording" && (
            <div className="testify-flex testify-items-center testify-gap-2 testify-text-red-500">
              <span className="testify-animate-pulse">●</span>
              <span className="testify-text-sm testify-font-medium">
                Recording
              </span>
            </div>
          )}
        </div>

        <CardDescription
          className="testify-text-base sm:testify-text-lg md:testify-text-xl testify-leading-relaxed"
          dangerouslySetInnerHTML={{ __html: data.responsePage.prompt }}
        />
      </div>

      {/* Rating Section */}
      <Show when={data.responsePage.shouldCollectStars}>
        <FormField
          control={form.control}
          name="rating"
          render={({ field }) => (
            <FormItem className="testify-space-y-2">
              <FormLabel className="testify-text-base sm:testify-text-lg">
                {data.labels.rating}
              </FormLabel>
              <FormControl>
                <div className="testify-flex testify-items-center testify-gap-2">
                  <StarRating
                    size="lg"
                    value={Number(field.value) || 0}
                    onChange={(value) => field.onChange(value)}
                  />
                  <span className="testify-text-sm testify-text-muted-foreground">
                    {Number(field.value) || 0}/5
                  </span>
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </Show>

      {/* Video Recording Section */}
      <div className="testify-space-y-4">
        <FormLabel className="testify-text-base sm:testify-text-lg">
          Record Your Video
        </FormLabel>

        {status === "idle" && mediaBlobUrl && (
          <Alert className="testify-flex testify-items-center">
            <AlertCircle className="testify-h-4 testify-w-4" />
            <AlertDescription>
              We&apos;ll need access to your camera and microphone to record
              your video testimonial
            </AlertDescription>
          </Alert>
        )}

        <div className="testify-relative testify-rounded-lg testify-overflow-hidden testify-bg-black testify-aspect-video">
          <AnimatePresence mode="wait">
            {previewStream && !mediaBlobUrl && !hasRecordingEnded && (
              <motion.video
                key={remountKey}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="testify-w-full testify-h-full testify-object-cover"
                ref={(video) => {
                  if (video && previewStream) {
                    video.srcObject = previewStream;
                    video.play();
                  }
                }}
              />
            )}
            {mediaBlobUrl && hasRecordingEnded && (
              <video
                className="testify-w-full testify-h-full testify-object-cover"
                src={mediaBlobUrl}
                controls
              />
            )}
            {!previewStream && !mediaBlobUrl && !hasRecordingEnded && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="testify-absolute testify-inset-0 testify-flex testify-flex-col testify-items-center testify-justify-center"
              >
                <VideoOff className="testify-h-10 testify-w-10 testify-text-white/60 testify-mb-2" />
                <p className="testify-text-white/80 testify-text-sm">
                  Camera preview will appear here
                </p>
              </motion.div>
            )}
          </AnimatePresence>

          {countdown !== null && (
            <motion.div
              className="testify-absolute testify-inset-0 testify-flex testify-items-center testify-justify-center testify-bg-black/70"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <motion.div
                key={countdown}
                initial={{ scale: 0.5, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 1.5, opacity: 0 }}
                className="testify-text-4xl sm:testify-text-6xl md:testify-text-7xl testify-font-bold testify-text-white"
              >
                {countdown === 3 && "3"}
                {countdown === 2 && "2"}
                {countdown === 1 && "Smile! 😊"}
              </motion.div>
            </motion.div>
          )}

          {status === "recording" && (
            <div className="testify-absolute testify-top-4 testify-right-4 testify-px-3 testify-py-1.5 testify-rounded-full">
              <div
                className="testify-flex testify-items-center testify-gap-2 testify-p-2 testify-rounded-full"
                style={{
                  background: styles["primaryColor"],
                }}
              >
                <Clock
                  className="testify-h-4 testify-w-4"
                  style={{
                    color: styles["primaryTextColor"],
                  }}
                />
                <span
                  className="testify-text-sm"
                  style={{
                    color: styles["primaryTextColor"],
                  }}
                >
                  {Math.floor(recordingTime / 60)}:
                  {(recordingTime % 60).toString().padStart(2, "0")}
                </span>
              </div>
            </div>
          )}
        </div>

        {status === "recording" && (
          <Progress
            value={(recordingTime / maxDuration) * 100}
            inidicatiorStyles={{
              background: styles["primaryColor"],
            }}
          />
        )}

        <div className="testify-flex testify-justify-between testify-items-center">
          <FormDescription>
            Maximum duration: {maxDuration} seconds
          </FormDescription>
          {mediaBlobUrl && (
            <Button variant="ghost" size="sm" onClick={resetRecording}>
              <RefreshCcw className="testify-h-4 testify-w-4 testify-mr-2" />
              Record Again
            </Button>
          )}
        </div>

        <div className="testify-flex testify-gap-3 testify-justify-center testify-w-full">
          {!mediaBlobUrl && (
            <Button
              size="lg"
              className="testify-w-full"
              variant={status === "recording" ? "destructive" : "default"}
              onClick={() =>
                status === "recording"
                  ? handleStopRecording()
                  : startCountdownAndRecord()
              }
              disabled={status === "acquiring_media"}
              style={{
                background: styles["primaryColor"],
                color: styles["primaryTextColor"],
              }}
            >
              {status === "acquiring_media" && (
                <Loader2 className="testify-h-4 testify-w-4 testify-mr-2 testify-animate-spin" />
              )}
              {status === "recording" ? "Stop Recording" : "Start Recording"}
            </Button>
          )}
        </div>
      </div>

      <Show when={!!mediaBlobUrl}>
        <Button
          className="testify-w-full"
          size="lg"
          disabled={!mediaBlobUrl}
          onClick={onNextStep}
          style={{
            background: styles["primaryColor"],
            color: styles["primaryTextColor"],
          }}
        >
          {data.labels.submitButton}
        </Button>
      </Show>
    </Card>
  );
};
